import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Save,
  RotateCcw,
  Search,
  BarChart3,
  ShieldCheck,
  Code2,
  CheckCircle2,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/seo")({
  component: SeoAdmin,
});

const STORE_KEY = "developer";
const DEFAULTS: any = { allow_indexing: true };

type FieldDef = {
  key: string;
  label: string;
  placeholder?: string;
  validate?: (v: string) => string | null;
  hint?: string;
};

const ANALYTICS_FIELDS: FieldDef[] = [
  { key: "ga_measurement_id", label: "Google Analytics 4", placeholder: "G-XXXXXXX", validate: (v) => (/^G-[A-Z0-9]{6,}$/i.test(v) ? null : "Expected G-XXXXXXX") },
  { key: "gtm_id", label: "Google Tag Manager", placeholder: "GTM-XXXXX", validate: (v) => (/^GTM-[A-Z0-9]{4,}$/i.test(v) ? null : "Expected GTM-XXXXX") },
  { key: "fb_pixel_id", label: "Meta / Facebook Pixel", placeholder: "1234567890", validate: (v) => (/^\d{6,}$/.test(v) ? null : "Numeric ID") },
  { key: "clarity_id", label: "Microsoft Clarity", placeholder: "abcdefghij" },
  { key: "hotjar_id", label: "Hotjar site ID", placeholder: "1234567", validate: (v) => (/^\d+$/.test(v) ? null : "Numeric ID") },
  { key: "plausible_domain", label: "Plausible domain", placeholder: "yourdomain.com" },
  { key: "posthog_key", label: "PostHog project key", placeholder: "phc_..." },
  { key: "posthog_host", label: "PostHog host (optional)", placeholder: "https://us.i.posthog.com" },
  { key: "sentry_dsn", label: "Sentry DSN (optional)", placeholder: "https://…@sentry.io/…" },
];

const VERIFY_FIELDS: FieldDef[] = [
  { key: "google_site_verification", label: "Google Search Console", placeholder: "content value" },
  { key: "bing_site_verification", label: "Bing Webmaster Tools" },
  { key: "yandex_site_verification", label: "Yandex Webmaster" },
  { key: "pinterest_site_verification", label: "Pinterest" },
  { key: "facebook_domain_verification", label: "Facebook domain" },
];

function SeoAdmin() {
  const qc = useQueryClient();
  const { data: initial, isLoading } = useQuery({
    queryKey: ["site_settings", STORE_KEY],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", STORE_KEY)
        .maybeSingle();
      if (error) throw error;
      return { ...DEFAULTS, ...((data?.value as any) ?? {}) };
    },
  });

  const [state, setState] = useState<any>(DEFAULTS);
  const [tab, setTab] = useState("indexing");
  const [showSecrets, setShowSecrets] = useState(false);
  useEffect(() => { if (initial) setState(initial); }, [initial]);

  const dirty = useMemo(
    () => JSON.stringify(state ?? {}) !== JSON.stringify(initial ?? {}),
    [state, initial]
  );

  const counts = useMemo(() => {
    const active = (fields: FieldDef[]) =>
      fields.filter((f) => (state?.[f.key] ?? "").toString().trim().length > 0).length;
    return {
      analytics: active(ANALYTICS_FIELDS),
      verify: active(VERIFY_FIELDS),
      snippets:
        (state?.custom_head_html?.trim() ? 1 : 0) +
        (state?.custom_body_html?.trim() ? 1 : 0),
    };
  }, [state]);

  const save = useMutation({
    mutationFn: async (value: any) => {
      const { error } = await supabase
        .from("site_settings")
        .upsert({ key: STORE_KEY, value });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["site_settings"] });
      qc.invalidateQueries({ queryKey: ["site_settings", STORE_KEY] });
      toast.success("SEO settings saved");
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  if (isLoading) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (patch: Record<string, any>) => setState({ ...state, ...patch });

  const indexing = state.allow_indexing !== false;

  return (
    <div className="space-y-6 pb-24 sm:pb-6">
      <AdminPageHeader
        title="SEO Configuration"
        description="Analytics, search-engine verification, indexing and custom scripts."
      />

      {/* Live status strip */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <StatusTile
          icon={indexing ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertCircle className="h-4 w-4 text-amber-500" />}
          label="Indexing"
          value={indexing ? "Allowed" : "Blocked"}
          tone={indexing ? "ok" : "warn"}
        />
        <StatusTile
          icon={<BarChart3 className="h-4 w-4 text-primary" />}
          label="Analytics"
          value={`${counts.analytics}/${ANALYTICS_FIELDS.length}`}
        />
        <StatusTile
          icon={<ShieldCheck className="h-4 w-4 text-primary" />}
          label="Verified"
          value={`${counts.verify}/${VERIFY_FIELDS.length}`}
        />
        <StatusTile
          icon={<Code2 className="h-4 w-4 text-primary" />}
          label="Snippets"
          value={`${counts.snippets}/2`}
        />
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between space-y-0">
          <div className="flex items-start gap-3">
            <div className="rounded-md border border-border bg-muted/50 p-2">
              <Search className="h-4 w-4 text-primary" />
            </div>
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                SEO Configuration
                {dirty && (
                  <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400">
                    <span className="mr-1 h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Unsaved
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>
                Analytics, search-engine verification, indexing and custom scripts.
              </CardDescription>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setShowSecrets((v) => !v)}>
              {showSecrets ? <EyeOff className="mr-1.5 h-3.5 w-3.5" /> : <Eye className="mr-1.5 h-3.5 w-3.5" />}
              {showSecrets ? "Hide" : "Reveal"} values
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="indexing" className="text-xs sm:text-sm">Indexing</TabsTrigger>
              <TabsTrigger value="analytics" className="text-xs sm:text-sm">
                Analytics
                {counts.analytics > 0 && <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{counts.analytics}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="verify" className="text-xs sm:text-sm">
                Verify
                {counts.verify > 0 && <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[10px]">{counts.verify}</Badge>}
              </TabsTrigger>
              <TabsTrigger value="snippets" className="text-xs sm:text-sm">Snippets</TabsTrigger>
            </TabsList>

            <TabsContent value="indexing" className="mt-4 space-y-4">
              <div className={`rounded-md border px-3 py-3 transition-colors ${indexing ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-foreground">Search-engine indexing</div>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      When disabled, every page emits <code className="rounded bg-muted px-1">noindex,nofollow</code>.
                    </p>
                  </div>
                  <Switch checked={indexing} onCheckedChange={(v) => set({ allow_indexing: v })} />
                </div>
              </div>
              <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
                <Sparkles className="mr-1.5 inline h-3.5 w-3.5" />
                Tip: leave indexing off while staging, then flip on at launch.
              </div>
            </TabsContent>

            <TabsContent value="analytics" className="mt-4">
              <FieldGrid fields={ANALYTICS_FIELDS} state={state} onChange={set} showSecrets={showSecrets} />
            </TabsContent>

            <TabsContent value="verify" className="mt-4 space-y-3">
              <p className="text-xs text-muted-foreground">
                Paste the <code className="rounded bg-muted px-1">content</code> value from each provider's verification meta tag.
              </p>
              <FieldGrid fields={VERIFY_FIELDS} state={state} onChange={set} showSecrets={showSecrets} />
            </TabsContent>

            <TabsContent value="snippets" className="mt-4 space-y-4">
              <SnippetField
                label="Custom <head> HTML"
                value={state.custom_head_html ?? ""}
                onChange={(v) => set({ custom_head_html: v })}
                placeholder="<!-- Pasted verbatim into the document head. Use for additional tracking, meta tags or fonts. -->"
              />
              <SnippetField
                label="Custom end-of-<body> HTML"
                value={state.custom_body_html ?? ""}
                onChange={(v) => set({ custom_body_html: v })}
                placeholder="<!-- Pasted before </body>. Use for chat widgets, GTM noscript, etc. -->"
              />
              <p className="text-xs text-muted-foreground">
                Changes apply to public pages within ~5 minutes (cached).
              </p>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Sticky action bar */}
      <div
        className={`fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 backdrop-blur sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none transition-all ${
          dirty ? "translate-y-0" : "translate-y-full sm:translate-y-0"
        }`}
      >
        <div className="mx-auto flex max-w-full items-center justify-between gap-2 px-4 py-3 sm:justify-end sm:px-0 sm:py-0">
          <span className="text-xs text-muted-foreground sm:hidden">
            {dirty ? "Unsaved changes" : "All saved"}
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => initial && setState(initial)}
              disabled={!dirty || save.isPending}
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Reset
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate({ ...(initial ?? {}), ...(state ?? {}) })}
              disabled={!dirty || save.isPending}
            >
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatusTile({
  icon, label, value, tone,
}: { icon: React.ReactNode; label: string; value: string; tone?: "ok" | "warn" }) {
  return (
    <div
      className={`rounded-md border px-3 py-2.5 transition-colors ${
        tone === "warn"
          ? "border-amber-500/30 bg-amber-500/5"
          : tone === "ok"
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "border-border bg-muted/30"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-foreground">{value}</div>
    </div>
  );
}

function FieldGrid({
  fields, state, onChange, showSecrets,
}: {
  fields: FieldDef[];
  state: any;
  onChange: (patch: Record<string, any>) => void;
  showSecrets: boolean;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => {
        const val = (state?.[f.key] ?? "") as string;
        const active = val.trim().length > 0;
        const err = active && f.validate ? f.validate(val) : null;
        return (
          <div key={f.key} className="grid gap-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">{f.label}</Label>
              {active && (
                err
                  ? <span className="text-[10px] text-amber-600 dark:text-amber-400">{err}</span>
                  : <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
              )}
            </div>
            <Input
              type={showSecrets || !active ? "text" : "password"}
              value={val}
              placeholder={f.placeholder}
              onChange={(e) => onChange({ [f.key]: e.target.value })}
              className={err ? "border-amber-500/60 focus-visible:ring-amber-500/30" : ""}
            />
          </div>
        );
      })}
    </div>
  );
}

function SnippetField({
  label, value, onChange, placeholder,
}: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  const lines = value ? value.split("\n").length : 0;
  const chars = value.length;
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <Label className="text-xs">{label}</Label>
        <span className="text-[10px] text-muted-foreground">{lines} lines · {chars} chars</span>
      </div>
      <Textarea
        rows={5}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-mono text-xs"
      />
    </div>
  );
}
